import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, RadioTower, RefreshCw, Send, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/common/Button'
import { Card } from '@/components/common/Card'
import { Input } from '@/components/common/Input'
import RcsService, {
  type RcsExecutionLogItem,
  type RcsSessionInfo,
  type RcsTasksSnapshot,
} from '@/services/rcs.service'

const STATUS_LABEL: Record<string, string> = {
  authenticated: 'Autenticado',
  pending_auth: 'Aguardando QR',
  disconnected: 'Desconectado',
  error: 'Erro',
}

function formatDate(epochSeconds?: number | null): string {
  if (!epochSeconds) return '—'
  return new Date(epochSeconds * 1000).toLocaleString('pt-BR')
}

function StatusPill({ value }: { value: string }) {
  const tone =
    value === 'authenticated'
      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
      : value === 'pending_auth'
        ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
        : value === 'error'
          ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
          : 'bg-slate-500/15 text-slate-300 border-slate-500/30'
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${tone}`}>
      {STATUS_LABEL[value] ?? value}
    </span>
  )
}

export function RcsTab() {
  const [snapshot, setSnapshot] = useState<RcsTasksSnapshot | null>(null)
  const [logs, setLogs] = useState<RcsExecutionLogItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [isAuthing, setIsAuthing] = useState(false)
  const [numberId, setNumberId] = useState('')
  const [phone, setPhone] = useState('')
  const [text, setText] = useState('')

  const loadAll = useCallback(async () => {
    try {
      const [snap, logsResp] = await Promise.all([
        RcsService.getTasks(),
        RcsService.getLogs(50),
      ])
      setSnapshot(snap)
      setLogs(logsResp.items)
    } catch (err) {
      toast.error('Falha ao carregar Monitoramento RCS')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAll()
    const id = setInterval(loadAll, 7_000)
    return () => clearInterval(id)
  }, [loadAll])

  const sessions: RcsSessionInfo[] = useMemo(() => snapshot?.sessions ?? [], [snapshot])

  async function handleExecute() {
    if (!numberId || !phone || !text) {
      toast.error('Preencha número-ID, telefone e mensagem.')
      return
    }
    setIsSending(true)
    try {
      const result = await RcsService.execute({ numberId, phone, text })
      if (result.success === true) {
        toast.success(`Enviado como ${result.messageType?.toUpperCase() ?? 'RCS'} (${result.durationMs} ms)`)
      } else {
        toast.error(result.error ?? 'Falha no envio RCS')
      }
      await loadAll()
    } catch (err) {
      toast.error('Erro ao executar envio RCS')
    } finally {
      setIsSending(false)
    }
  }

  async function handleAuth() {
    if (!numberId) {
      toast.error('Informe um número-ID para iniciar autenticação.')
      return
    }
    setIsAuthing(true)
    try {
      await RcsService.startAuth(numberId)
      toast.success('Autenticação iniciada — escaneie o QR no Google Messages.')
      await loadAll()
    } catch (err) {
      toast.error('Erro ao iniciar autenticação RCS')
    } finally {
      setIsAuthing(false)
    }
  }

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <RadioTower className="text-[var(--color-brand)]" size={18} />
          <h2 className="text-base font-semibold text-[var(--text-primary)]">Disparo RCS</h2>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Input
            label="Número-ID (sessão)"
            value={numberId}
            onChange={(e) => setNumberId(e.target.value)}
            placeholder="ex: numero-01"
          />
          <Input
            label="Telefone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+55 11 9XXXX-XXXX"
          />
          <div className="flex items-end gap-2">
            <Button onClick={handleAuth} disabled={isAuthing} variant="outline">
              {isAuthing ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
              <span className="ml-2">Autenticar</span>
            </Button>
            <Button onClick={handleExecute} disabled={isSending}>
              {isSending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              <span className="ml-2">Enviar</span>
            </Button>
          </div>
        </div>
        <textarea
          className="mt-3 w-full rounded-md border border-[var(--border-color)] bg-[var(--bg-input)] p-2 text-sm text-[var(--text-primary)]"
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Mensagem RCS..."
        />
      </Card>

      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-[var(--text-primary)]">Sessões</h2>
          <Button variant="ghost" onClick={loadAll} disabled={isLoading}>
            <RefreshCw className={isLoading ? 'animate-spin' : ''} size={14} />
            <span className="ml-1 text-xs">Atualizar</span>
          </Button>
        </div>
        {sessions.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">
            Nenhuma sessão RCS registrada. Use "Autenticar" para iniciar uma.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-[var(--text-muted)]">
                <tr>
                  <th className="py-2">Sessão</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">QR</th>
                  <th className="py-2">Autenticado em</th>
                  <th className="py-2">Erro</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.sessionId} className="border-t border-[var(--border-color)]">
                    <td className="py-2 font-mono text-xs">{s.sessionId}</td>
                    <td className="py-2">
                      <StatusPill value={s.status} />
                    </td>
                    <td className="py-2 text-[var(--text-secondary)]">{formatDate(s.lastQrAt)}</td>
                    <td className="py-2 text-[var(--text-secondary)]">
                      {formatDate(s.lastAuthenticatedAt)}
                    </td>
                    <td className="py-2 text-rose-300">{s.lastError ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h2 className="mb-3 text-base font-semibold text-[var(--text-primary)]">
          Execuções recentes
        </h2>
        {logs.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">Nenhuma execução registrada.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-[var(--text-muted)]">
                <tr>
                  <th className="py-2">Quando</th>
                  <th className="py-2">Sessão</th>
                  <th className="py-2">Telefone</th>
                  <th className="py-2">Tipo</th>
                  <th className="py-2">Resultado</th>
                  <th className="py-2">Latência</th>
                  <th className="py-2">Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((item) => (
                  <tr key={item.id} className="border-t border-[var(--border-color)]">
                    <td className="py-2 text-xs">{formatDate(item.startedAt)}</td>
                    <td className="py-2 font-mono text-xs">{item.sessionId}</td>
                    <td className="py-2">{item.phone}</td>
                    <td className="py-2 uppercase">{item.messageType}</td>
                    <td className="py-2">
                      {item.success ? (
                        <span className="text-emerald-400">OK</span>
                      ) : (
                        <span className="text-rose-400">{item.error ?? 'Falha'}</span>
                      )}
                    </td>
                    <td className="py-2 tabular-nums">{item.durationMs} ms</td>
                    <td className="py-2 max-w-xs truncate text-[var(--text-secondary)]">
                      {item.textPreview}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
